/**
 * Treatments catalogue cache — stale-while-revalidate.
 *
 * Why this exists:
 *   The Catalogue page used to render an empty grid for 100 ms–3 s on
 *   every visit while `GET /api/treatments` was in flight. We now seed
 *   the initial React state from a (module-scoped + localStorage)
 *   cache so repeat visits paint instantly, and keep refetching in
 *   the background so admin price/content edits land within one
 *   round-trip without the customer seeing a blank list.
 *
 * Freshness contract:
 *   - Cards appear immediately from cache (any age).
 *   - A fresh fetch is still issued on every mount + visibility/focus
 *     event, so admin edits propagate within one request. Carts and
 *     the checkout endpoint always read live backend data, so a stale
 *     cache can never cause a wrong price to be charged.
 */

const KEY = "clae_catalogue_cache_v1";

// Module-scoped memory cache — survives SPA navigations without a
// localStorage round-trip. Rebuilt from localStorage on first import
// (SSR-safe guard for the window reference).
let mem = null;
if (typeof window !== "undefined") {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) mem = JSON.parse(raw);
  } catch (_) { mem = null; }
}

export function readCache() {
  // Shape: { treatments: [...], promoMap: {...}, promoList: [...], at: iso }
  return mem || { treatments: [], promoMap: {}, promoList: [], at: null };
}

export function writeTreatments(treatments) {
  mem = { ...(mem || {}), treatments: treatments || [], at: new Date().toISOString() };
  _persist();
}

export function writePromotions(promoMap, promoList) {
  mem = { ...(mem || {}), promoMap: promoMap || {}, promoList: promoList || [] };
  _persist();
}

function _persist() {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(KEY, JSON.stringify(mem)); } catch (_) { /* quota / private mode — ignore */ }
}
